/**
 * Platform WhatsApp gateway (Evolution API) schemas.
 *
 * Backs the /api/v1/platform/whatsapp surface — the platform owner's
 * dashboard for the self-hosted Evolution API gateway (one "instance" =
 * one WhatsApp login). Distinct from `whatsapp.ts`, which is the TENANT
 * click-to-send wa.me feature.
 */

import { z } from 'zod';

// -----------------------------------------------------------------------------
// Inputs
// -----------------------------------------------------------------------------

/** Instance name — also a URL path segment on the gateway, so keep it strict
 *  (the lookahead rejects dot-only names like "." / ".." — path semantics). */
export const WaInstanceNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^(?!\.+$)[a-zA-Z0-9._-]+$/, 'letters, digits, dot, underscore and hyphen only');

export const WaInstanceCreateSchema = z.object({
  name: WaInstanceNameSchema,
});
export type WaInstanceCreate = z.infer<typeof WaInstanceCreateSchema>;

/** Test-message body — number normalizes to digits (drops +, spaces, dashes). */
export const WaTestMessageSchema = z.object({
  number: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .pipe(z.string().regex(/^\d{6,20}$/, 'expected 6-20 digits')),
  text: z.string().trim().min(1).max(4096),
});
export type WaTestMessage = z.infer<typeof WaTestMessageSchema>;

// -----------------------------------------------------------------------------
// Responses (mapped server-side from Evolution's raw shapes)
// -----------------------------------------------------------------------------

/** One gateway instance. `token` is the PER-INSTANCE key (safe to reveal to the
 *  platform admin for use in other projects) — never the gateway master key. */
export interface WaInstance {
  name: string;
  /** 'open' (connected) | 'connecting' (QR pending) | 'close' (logged out). */
  state: string;
  ownerPhone: string | null;
  profileName: string | null;
  profilePictureUrl: string | null;
  token: string | null;
}

/** QR/pairing payload. `qrBase64` is a full data URI (render directly in <img src>);
 *  both fields are null once the instance is already connected (state 'open'). */
export interface WaQr {
  state: string;
  qrBase64: string | null;
  pairingCode: string | null;
}

export interface WaInstanceCreated extends WaQr {
  name: string;
  token: string | null;
}

/** GET /config — `configured` is false (not an error) when the env vars are unset. */
export interface WaGatewayConfig {
  configured: boolean;
  serverUrl: string | null;
  reachable: boolean;
  version: string | null;
}

/** One message in an instance's recent history (for the test console). */
export interface WaMessage {
  id: string;
  /** true = sent by this instance; false = received. */
  fromMe: boolean;
  /** The other party's phone (digits), or null. */
  number: string | null;
  /** Body text, or a `[type]` label for media/other. */
  text: string;
  /** Unix seconds. */
  timestamp: number;
  /** Delivery status (PENDING / SERVER_ACK / DELIVERY_ACK / READ …), or null. */
  status: string | null;
}

/** One recent conversation for the test console — a contact plus a few of its
 *  latest messages (derived from stored messages, grouped by counterparty). */
export interface WaChat {
  /** The counterparty JID (e.g. 9647...@s.whatsapp.net or a group @g.us). */
  remoteJid: string;
  /** Counterparty phone (digits), or null. */
  number: string | null;
  /** WhatsApp display name if seen, else null. */
  name: string | null;
  /** Unix seconds of the latest message in the chat (for ordering). */
  lastTimestamp: number;
  /** A few most-recent messages, oldest → newest. */
  messages: WaMessage[];
}
